import { type CommunityDetail, getUserName } from 'models';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Delete2LineIcon, Modal, message } from 'ui';
import { useDeleteUserFromCommunity } from '../../../queries';
import type { User } from '../../user-list';

type P = {
  community: CommunityDetail;
};

export const useDeleteMember = ({ community }: P) => {
  const { t } = useTranslation();

  const [modal, contextHolder] = Modal.useModal();
  const { mutateAsync: deleteUser } = useDeleteUserFromCommunity();

  const confirm = useCallback(
    (user: User) => {
      modal.confirm({
        content: 'This action is irreversible.',
        icon: <Delete2LineIcon type="error" />,
        okText: 'Yes, remove',
        async onOk() {
          try {
            await deleteUser(
              {
                communityId: community.id,
                userId: user.id,
              },
              {
                onError() {
                  message.error(t('Something went wrong. The user could’t be removed.'));
                },
                onSuccess() {
                  message.success(t('User removed successfully'));
                },
              },
            );
          } catch {}
        },
        title: t('Remove user from?', {
          fullName: getUserName(user),
          groupName: community.name,
        }),
        type: 'error',
      });
    },
    [community.id, community.name, deleteUser, modal, t],
  );

  return useMemo(() => ({ confirm, contextHolder }), [confirm, contextHolder]);
};
